import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../services/apiClient';

// ───────────────────────────────────────────────────────────────────────────────
// Scheduled Workflows — the Big Picture's second table.
//
// Directional Drilling, Vertical Drilling, Loops and Small Jobs: one row per CARD, each
// with a start, a length in working days and the finish that follows. Independent of
// the job's dirt / electrical dates on purpose — those answer "when is the job"; this
// answers "when is the rig".
//
// LIST puts the dates in. CALENDAR is the reason to have them (Pat, Sep 24 2026 — a
// wall calendar in place of the earlier timeline): every card sits on the days it runs,
// and two cards on the same drill on the same day go red, so a double-booking shows
// three weeks out instead of on the morning it happens.
// ───────────────────────────────────────────────────────────────────────────────

const TYPES = [
  { key: 'horizontal_drilling', label: 'Directional Drilling', tint: '#7c3aed' },
  { key: 'vertical_drilling',   label: 'Vertical Drilling',    tint: '#1d4ed8' },
  { key: 'loops',               label: 'Loops',                tint: '#0f766e' },
  { key: 'small_jobs',          label: 'Small Jobs',           tint: '#b45309' },
];
const TINT = Object.fromEntries(TYPES.map(t => [t.key, t.tint]));

const pad = n => String(n).padStart(2, '0');
const ymd = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const at = s => new Date(`${s}T12:00:00`);
const short = s => (s ? at(s).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '—');
const isWork = d => d.getDay() !== 0 && d.getDay() !== 6;

const sel = { fontSize: 12.5, padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', cursor: 'pointer' };
const th = { textAlign: 'left', padding: '9px 10px', fontSize: 11, fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap', borderBottom: '1px solid var(--border)', cursor: 'pointer', userSelect: 'none' };
const td = { padding: '7px 10px', fontSize: 13, color: 'var(--text)', borderBottom: '1px solid #f3f4f6', verticalAlign: 'middle' };
const box = { border: '1px solid #e5e7eb', borderRadius: 6, padding: '5px 7px', fontSize: 13, fontFamily: 'inherit', background: '#fff', color: '#111' };

function Segmented({ options, value, onPick, tint = '#1e3a5f' }) {
  return (
    <div style={{ display: 'inline-flex', border: '1px solid var(--border)', borderRadius: 9, overflow: 'hidden' }}>
      {options.map(o => (
        <button key={o.k} onClick={() => onPick(o.k)}
          style={{ padding: '6px 13px', fontSize: 12.5, fontWeight: 700, border: 'none', cursor: 'pointer', background: value === o.k ? (o.tint || tint) : 'var(--surface)', color: value === o.k ? '#fff' : 'var(--text)' }}>{o.label}</button>
      ))}
    </div>
  );
}

// A number box that saves when you leave it, not on every keystroke.
function DaysBox({ value, disabled, onSave }) {
  const [text, setText] = useState(value == null ? '' : String(value));
  useEffect(() => { setText(value == null ? '' : String(value)); }, [value]);
  // Whole or half days: 0.5, 1, 1.5 … (a loop can be half a day).
  return <input type="text" inputMode="decimal" disabled={disabled} value={text} placeholder="—"
    onChange={e => setText(e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1'))}
    onBlur={() => { if (text !== (value == null ? '' : String(value))) onSave(text === '' ? null : Math.round(Number(text) * 2) / 2); }}
    onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
    style={{ ...box, width: 58, textAlign: 'right', background: disabled ? '#f9fafb' : '#fff' }} />;
}

// Drop a leading "<number> - " from a job name so the number is not said twice on a chip.
const shortName = n => String(n || '').replace(/^\s*\S+\s*-\s*/, '');

// fixedType: the page for ONE workflow (Loops, a drill, Small Jobs) — no workflow
// switch, only List / Calendar (Pat, Sep 24 2026, "slice two").
export default function WorkflowSchedule({ header, fixedType = null }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [view, setView] = useState('list');             // list | cards | calendar
  const [groupBy, setGroupBy] = useState('month');      // cards view: 'status' | 'month'
  const [typeFilter, setTypeFilter] = useState(fixedType || '');
  const [pmFilter, setPmFilter] = useState('');            // one PM's cards, same as the dirt/electrical table
  const [search, setSearch] = useState('');
  const [showDone, setShowDone] = useState(false);
  const [sort, setSort] = useState({ key: 'start', dir: 1 });
  const [busy, setBusy] = useState('');

  const load = useCallback(() => apiFetch(`/workflow-schedule${showDone ? '?all=1' : ''}`)
    .then(d => { setData(d); setErr(''); })
    .catch(e => setErr(e?.error || 'Could not load the workflow schedule.')), [showDone]);
  useEffect(() => { load(); }, [load]);

  const canEdit = !!data?.can_edit;
  async function save(row, patch) {
    setBusy(row.key); setErr('');
    try { await apiFetch(`/workflow-schedule/${row.kind}/${row.id}`, { method: 'PUT', body: JSON.stringify(patch) }); await load(); }
    catch (e) { setErr(e?.error || e?.message || 'That did not save.'); }
    setBusy('');
  }
  const open = r => navigate(r.kind === 'wo' ? `/admin/workflows/small_jobs?card=${r.id}` : `/admin/workflows/${r.type}?card=${r.id}`);
  // The status board's drop: the same call the workflow boards make when a card is dragged.
  async function setStatus(row, status) {
    setBusy(row.key); setErr('');
    try {
      if (row.kind === 'wo') await apiFetch(`/work-orders/${row.id}`, { method: 'PATCH', body: JSON.stringify({ wo_status: status }) });
      else await apiFetch(`/projects/${row.project_id}/sub-jobs/${row.id}`, { method: 'PATCH', body: JSON.stringify({ status }) });
      await load();
    } catch (e) { setErr(e?.error || e?.message || 'That did not save.'); }
    setBusy('');
  }

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = (data?.rows || []).filter(r => (!typeFilter || r.type === typeFilter)
      && (!pmFilter || r.pm_name === pmFilter)
      && (!q || `${r.job_number} ${r.job_name} ${r.name} ${r.pm_name} ${r.rig || ''}`.toLowerCase().includes(q)));
    const val = r => ({ job: `${r.job_number || ''} ${r.job_name || ''}`, type: r.type_label, rig: r.rig || '~', pm: r.pm_name || '~', status: statusOf(r).label,
      start: r.start || '9999', days: r.days || 0, end: (r.start && r.end) || '9999' }[sort.key]);
    return list.slice().sort((a, b) => { const A = val(a), B = val(b); return (A < B ? -1 : A > B ? 1 : 0) * sort.dir || String(a.job_number).localeCompare(String(b.job_number)); });
  }, [data, typeFilter, pmFilter, search, sort]);
  const pmOptions = useMemo(() => [...new Set((data?.rows || []).map(r => r.pm_name).filter(Boolean))].sort(), [data]);

  const clashCount = useMemo(() => (data?.rows || []).filter(r => r.clashes?.length).length, [data]);
  const head = (label, key, extra) => (
    <th style={{ ...th, ...extra }} onClick={() => setSort(s => ({ key, dir: s.key === key ? -s.dir : 1 }))}>
      {label}{sort.key === key ? (sort.dir > 0 ? ' ▲' : ' ▼') : ''}
    </th>
  );

  // One list per workflow (Pat: "it should be one list for each work flow"). Each is the
  // same table — job, PM, start, working days, finish — and the two drilling lists carry
  // the drill, because that's what gets double-booked.
  const listFor = (t, list) => {
    const hasRig = t.key === 'horizontal_drilling' || t.key === 'vertical_drilling';
    return (
    <div key={t.key} style={{ marginBottom: 22 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, margin: '0 2px 8px' }}>
        <span style={{ width: 10, height: 10, borderRadius: 3, background: t.tint, alignSelf: 'center' }} />
        <h2 style={{ fontSize: 15.5, fontWeight: 800, margin: 0, color: 'var(--text)' }}>{t.label}</h2>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{list.length} card{list.length === 1 ? '' : 's'}{list.some(r => r.clashes?.length) ? ' · ' : ''}</span>
        {list.some(r => r.clashes?.length) && <span style={{ fontSize: 12, fontWeight: 800, color: '#b91c1c' }}>⚠ {list.filter(r => r.clashes?.length).length} double-booked</span>}
      </div>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderTop: `3px solid ${t.tint}`, borderRadius: 12, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
            <thead><tr>
              {head('Job', 'job')}{hasRig && head('Drill', 'rig')}{head('PM', 'pm')}{head('Status', 'status')}
              {head('Start', 'start')}{head('Working days', 'days', { textAlign: 'right' })}{head('Finish', 'end')}
              <th style={{ ...th, cursor: 'default' }} />
            </tr></thead>
            <tbody>
              {list.length === 0 && <tr><td colSpan={8} style={{ ...td, color: '#9ca3af', padding: 18 }}>No {t.label.toLowerCase()} cards{showDone ? '' : ' open'} right now.</td></tr>}
              {list.map(r => {
                const clash = r.clashes?.length > 0;
                const off = !canEdit || busy === r.key || r.done;
                return (
                  <tr key={r.key} style={{ background: clash ? '#fef2f2' : r.done ? '#fafafa' : 'transparent', opacity: r.done ? 0.65 : 1 }}>
                    <td style={td}>
                      <button onClick={() => open(r)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', fontSize: 13, fontWeight: 700, color: '#1d4ed8' }}>
                        <span style={{ fontFamily: 'monospace', color: '#e87722', marginRight: 6 }}>{r.job_number}</span>{String(r.job_name || '').replace(new RegExp(`^${r.job_number}\\s*[-–]\\s*`), '')}
                      </button>
                      {r.done && <span style={{ fontSize: 11, color: '#0f766e', fontWeight: 700, marginLeft: 8 }}>done</span>}
                      {r.kind === 'card' && r.name && r.name !== r.type_label && <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{r.name}</div>}
                    </td>
                    {hasRig && (
                    <td style={td}>
                      {(
                        <select disabled={off} value={r.rig || ''} onChange={e => save(r, { rig: e.target.value || null })} style={{ ...box, padding: '4px 6px', fontWeight: r.rig ? 700 : 400, color: r.rig ? '#111' : '#9ca3af' }}>
                          <option value="">No drill yet</option>
                          {((data.rigs_by_type && data.rigs_by_type[t.key]) || data.rigs || []).concat(r.rig && !((data.rigs_by_type && data.rigs_by_type[t.key]) || data.rigs || []).includes(r.rig) ? [r.rig] : []).map(g => <option key={g} value={g}>{g}</option>)}
                        </select>
                      )}
                    </td>
                    )}
                    <td style={{ ...td, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{r.pm_name || '—'}</td>
                    <td style={td}>
                      {/* The card's status, right on the list (Ryan, Oct 31 2026) — changing it is the same as dragging it on the Cards board. */}
                      {(() => {
                        const st = statusOf(r);
                        const opts = r.kind === 'wo' ? WO_STATUSES : CARD_STATUSES;
                        return (
                          <select disabled={!canEdit || busy === r.key || (r.done && !opts.some(o => o.key === r.status))} value={r.status || ''} onChange={e => setStatus(r, e.target.value)}
                            style={{ ...box, padding: '4px 6px', fontWeight: 800, fontSize: 12.5, color: st.tint, borderColor: st.tint + '66', background: st.tint + '12', cursor: canEdit ? 'pointer' : 'default' }}>
                            {!opts.some(o => o.key === r.status) && <option value={r.status || ''}>{st.label}</option>}
                            {opts.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
                          </select>
                        );
                      })()}
                    </td>
                    <td style={td}><input type="date" disabled={off} value={r.start || ''} onChange={e => save(r, { start: e.target.value || null })} style={{ ...box, background: off ? '#f9fafb' : '#fff' }} /></td>
                    <td style={{ ...td, textAlign: 'right' }}><DaysBox value={r.days} disabled={off || !r.start} onSave={v => save(r, { days: v })} /></td>
                    <td style={td}>
                      <input type="date" disabled={off || !r.start} min={r.start || undefined} value={(r.start && r.days && r.end) || ''} onChange={e => e.target.value && save(r, { end: e.target.value })} style={{ ...box, background: off || !r.start ? '#f9fafb' : '#fff' }} />
                    </td>
                    <td style={{ ...td, fontSize: 12, fontWeight: 700, color: '#b91c1c', maxWidth: 230 }}>
                      {clash && <>⚠ {r.rig} is also on {r.clashes.map(c => c.job_number || c.job_name).join(', ')}</>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
    </div>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 4 }}>
        {header}
        <Segmented options={[{ k: 'list', label: 'List' }, { k: 'cards', label: 'Cards' }, { k: 'calendar', label: 'Calendar' }]} value={view} onPick={setView} tint="#c2410c" />
        {view === 'cards' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Columns</span>
            <Segmented tint="#1e3a5f" value={groupBy} onPick={setGroupBy} options={[{ k: 'status', label: 'Status' }, { k: 'month', label: 'Month' }]} />
          </div>
        )}
        {!fixedType && <Segmented tint="#1e3a5f" value={typeFilter} onPick={setTypeFilter}
          options={[{ k: '', label: 'All four' }, ...TYPES.map(t => ({ k: t.key, label: t.label, tint: t.tint }))]} />}
        <select value={pmFilter} onChange={e => setPmFilter(e.target.value)} title="Show one PM's cards"
          style={{ ...sel, fontWeight: 700, borderColor: pmFilter ? '#1e3a5f' : 'var(--border)', background: pmFilter ? '#eff6ff' : 'var(--surface)', color: pmFilter ? '#1e3a5f' : 'var(--text)' }}>
          <option value="">All PMs</option>
          {pmOptions.map(nm => <option key={nm} value={nm}>{nm}</option>)}
        </select>
        <input type="text" placeholder="Search job, PM or rig…" value={search} onChange={e => setSearch(e.target.value)} style={{ ...sel, cursor: 'text', minWidth: 180, flex: '0 1 240px' }} />
        <label style={{ fontSize: 12.5, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
          <input type="checkbox" checked={showDone} onChange={e => setShowDone(e.target.checked)} /> Show completed
        </label>
        <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 'auto' }}>{rows.length} card{rows.length === 1 ? '' : 's'}{!canEdit && data ? ' · view only' : ''}</span>
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 12 }}>
        {view === 'list'
          ? <>A list for each workflow, one row per card. The start is the card’s own date; give it a length in <b>working days</b> and the finish follows — or type the finish and the length follows. These are separate from the job’s dirt and electrical dates.</>
          : view === 'cards'
          ? (groupBy === 'month'
            ? <>A column per month, a year out. <b>Drag a card into a month</b> to put it there — it lands on the first working day and keeps its length; sharpen the day on the List later. Drop it on "No date yet" to take the date off.</>
            : <>A column per status. <b>Drag a card between columns</b> to change its status — the same statuses as the workflow boards. Drilling and loops cards have their own set; small jobs have theirs.</>)
          : <>Every card on the days it runs, colored by workflow, with the drill on it. Two cards on the same drill on the same day turn red. Click a card to open it; click a day's "more" for the whole day.</>}
      </div>

      {clashCount > 0 && (
        <div style={{ background: '#fef2f2', border: '1.5px solid #fca5a5', borderRadius: 10, padding: '10px 14px', marginBottom: 12, fontSize: 13, color: '#991b1b', fontWeight: 700 }}>
          ⚠ {clashCount} card{clashCount === 1 ? ' is' : 's are'} double-booked — the same drill on two jobs over the same days.
          {view === 'list' && <button onClick={() => setView('calendar')} style={{ marginLeft: 10, background: 'none', border: 'none', color: '#b91c1c', textDecoration: 'underline', fontWeight: 800, cursor: 'pointer', fontSize: 13 }}>See it on the timeline</button>}
        </div>
      )}
      {err && <div style={{ fontSize: 12.5, color: '#b91c1c', fontWeight: 600, marginBottom: 10 }}>{err}</div>}
      {!data && !err && <div style={{ padding: 30, color: '#6b7280' }}>Loading the workflows…</div>}

      {data && view === 'list' && TYPES.filter(t => !typeFilter || t.key === typeFilter).map(t => listFor(t, rows.filter(r => r.type === t.key)))}

      {data && view === 'cards' && <CardsView rows={rows} groupBy={groupBy} canEdit={canEdit} onOpen={open} onSave={save} onStatus={setStatus} onlyType={typeFilter || null} />}
      {data && view === 'calendar' && <CalendarView rows={rows} onOpen={open} onlyType={typeFilter || null} />}
    </div>
  );
}

// ONE workflow's card board, for the schedule home's Cards (Ryan, Oct 29 2026: "for the Cards,
// select one workflow at a time"). Same board as above — columns by Status or by Month, drag to
// move — narrowed by the home page's PM boxes (`pmShown(name)`) and search.
export function WorkflowCards({ type, pmShown = () => true, search = '' }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [groupBy, setGroupBy] = useState('status');
  const [showDone, setShowDone] = useState(false);
  const load = useCallback(() => apiFetch(`/workflow-schedule${showDone ? '?all=1' : ''}`)
    .then(d => { setData(d); setErr(''); })
    .catch(e => setErr(e?.error || 'Could not load the cards.')), [showDone]);
  useEffect(() => { load(); }, [load]);
  const canEdit = !!data?.can_edit;
  async function save(row, patch) {
    setErr('');
    try { await apiFetch(`/workflow-schedule/${row.kind}/${row.id}`, { method: 'PUT', body: JSON.stringify(patch) }); await load(); }
    catch (e) { setErr(e?.error || e?.message || 'That did not save.'); }
  }
  async function setStatus(row, status) {
    setErr('');
    try {
      if (row.kind === 'wo') await apiFetch(`/work-orders/${row.id}`, { method: 'PATCH', body: JSON.stringify({ wo_status: status }) });
      else await apiFetch(`/projects/${row.project_id}/sub-jobs/${row.id}`, { method: 'PATCH', body: JSON.stringify({ status }) });
      await load();
    } catch (e) { setErr(e?.error || e?.message || 'That did not save.'); }
  }
  const open = r => navigate(r.kind === 'wo' ? `/admin/workflows/small_jobs?card=${r.id}` : `/admin/workflows/${r.type}?card=${r.id}`);
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data?.rows || []).filter(r => r.type === type && pmShown(r.pm_name)
      && (!q || `${r.job_number} ${r.job_name} ${r.name} ${r.pm_name} ${r.rig || ''}`.toLowerCase().includes(q)));
  }, [data, type, pmShown, search]);
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Columns</span>
          <Segmented tint="#1e3a5f" value={groupBy} onPick={setGroupBy} options={[{ k: 'status', label: 'Status' }, { k: 'month', label: 'Month' }]} />
        </div>
        <label style={{ fontSize: 12.5, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
          <input type="checkbox" checked={showDone} onChange={e => setShowDone(e.target.checked)} /> Show completed
        </label>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{rows.length} card{rows.length === 1 ? '' : 's'}{!canEdit && data ? ' · view only' : ''}</span>
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 12 }}>
        {groupBy === 'month'
          ? <>A column per month, a year out. <b>Drag a card into a month</b> to put it there — it lands on the first working day and keeps its length. Drop it on "No date yet" to take the date off.</>
          : <>A column per status. <b>Drag a card between columns</b> to change its status — the same statuses as the workflow board.</>}
      </div>
      {err && <div style={{ fontSize: 12.5, color: '#b91c1c', fontWeight: 600, marginBottom: 10 }}>{err}</div>}
      {!data && !err && <div style={{ padding: 30, color: '#6b7280' }}>Loading the cards…</div>}
      {data && <CardsView rows={rows} groupBy={groupBy} canEdit={canEdit} onOpen={open} onSave={save} onStatus={setStatus} onlyType={type} />}
    </div>
  );
}

// ── Cards ────────────────────────────────────────────────────────────────────
// The Big Picture's Cards board for the workflows (Pat, Sep 25 2026): columns by
// STATUS, like the workflow boards, or by MONTH a year out — so a job that is way out
// can be dragged onto its month in one move and sharpened to a day on the List later.
const CARD_STATUSES = [
  { key: 'pending',  label: 'Not Started', tint: '#6b7280' },
  { key: 'active',   label: 'Scheduled',   tint: '#1d4ed8' },
  { key: 'on_hold',  label: 'On Hold',     tint: '#b45309' },
  { key: 'complete', label: 'Complete',    tint: '#15803d' },
];
const WO_STATUSES = [
  { key: 'week_electrical',   label: 'This Week · Electrical', tint: '#1d4ed8' },
  { key: 'week_dirt',         label: 'This Week · Dirt',       tint: '#b45309' },
  { key: 'inside_electrical', label: 'Inside Electrical',      tint: '#0e7490' },
  { key: 'waiting_material',  label: 'Waiting On Material',    tint: '#9333ea' },
  { key: 'next_week',         label: 'Next Week Or Longer',    tint: '#475569' },
  { key: 'pole_hold',         label: 'Pole Hold',              tint: '#dc2626' },
];
// A row's status as a label + colour, for the List (a finished small job may be in a billing column not listed here).
function statusOf(r) {
  const hit = (r.kind === 'wo' ? WO_STATUSES : CARD_STATUSES).find(o => o.key === r.status);
  if (hit) return hit;
  const label = String(r.status || 'Not set').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  return { key: r.status, label, tint: r.done ? '#15803d' : '#6b7280' };
}
const NO_DATE = '__none', EARLIER = '__earlier';
const monthKeyOf = (start, todayKey) => (!start ? NO_DATE : start.slice(0, 7) < todayKey.slice(0, 7) ? EARLIER : start.slice(0, 7));
const monthLabel = k => (k === NO_DATE ? 'No date yet' : k === EARLIER ? 'Earlier' : new Date(k + '-01T12:00:00').toLocaleDateString('en-US', { month: 'long', year: 'numeric' }));
// The first working day of a month — or today, when the month is this one and today is past the first.
function firstWorkingDay(monthKey, today) {
  const d = at(monthKey + '-01');
  if (monthKey === today.slice(0, 7) && at(today) > d) d.setTime(at(today).getTime());
  while (!isWork(d)) d.setDate(d.getDate() + 1);
  return ymd(d);
}

function CardsView({ rows, groupBy, canEdit, onOpen, onSave, onStatus, onlyType }) {
  const [over, setOver] = useState(null);
  const today = ymd(new Date());
  const hasCards = rows.some(r => r.kind === 'card'), hasWos = rows.some(r => r.kind === 'wo');

  const columns = useMemo(() => {
    if (groupBy === 'status') {
      const cardCols = (!onlyType || onlyType !== 'small_jobs') && (hasCards || (onlyType && onlyType !== 'small_jobs')) ? CARD_STATUSES.map(c => ({ ...c, key: 'card:' + c.key, for: 'card', status: c.key })) : [];
      const woCols = (!onlyType || onlyType === 'small_jobs') && (hasWos || onlyType === 'small_jobs') ? WO_STATUSES.map(c => ({ ...c, key: 'wo:' + c.key, for: 'wo', status: c.key })) : [];
      return [...cardCols, ...woCols];
    }
    const first = new Date(); first.setDate(1); first.setHours(12, 0, 0, 0);
    const months = Array.from({ length: 12 }, (_, i) => { const m = new Date(first.getFullYear(), first.getMonth() + i, 1, 12); return ymd(m).slice(0, 7); });
    const extra = [...new Set(rows.map(r => monthKeyOf(r.start, today)))].filter(k => k !== NO_DATE && k !== EARLIER && !months.includes(k)).sort();
    return [
      { key: NO_DATE, label: monthLabel(NO_DATE), tint: '#94a3b8' },
      { key: EARLIER, label: monthLabel(EARLIER), tint: '#b91c1c' },
      ...months.map(k => ({ key: k, label: monthLabel(k), tint: k === today.slice(0, 7) ? '#e87722' : '#1d4ed8' })),
      ...extra.map(k => ({ key: k, label: monthLabel(k), tint: '#1d4ed8' })),
    ];
  }, [groupBy, rows, onlyType, hasCards, hasWos, today]);

  const colOf = r => (groupBy === 'status' ? `${r.kind}:${r.status}` : monthKeyOf(r.start, today));
  const byCol = useMemo(() => {
    const out = Object.fromEntries(columns.map(c => [c.key, []]));
    for (const r of rows) { const k = colOf(r); if (out[k]) out[k].push(r); else if (groupBy === 'status') (out[`${r.kind}:${r.kind === 'wo' ? 'next_week' : 'pending'}`] || []).push(r); }
    for (const k of Object.keys(out)) out[k].sort((a, b) => String(a.start || '9999').localeCompare(String(b.start || '9999')) || String(a.job_number).localeCompare(String(b.job_number)));
    return out;
  }, [rows, columns, groupBy, today]); // eslint-disable-line react-hooks/exhaustive-deps

  function drop(e, col) {
    e.preventDefault(); setOver(null);
    if (!canEdit) return;
    let r; try { r = JSON.parse(e.dataTransfer.getData('text/plain')); } catch { return; }
    const row = rows.find(x => x.key === r.key); if (!row) return;
    if (colOf(row) === col.key) return;
    if (groupBy === 'status') { if (col.for !== row.kind) return; onStatus(row, col.status); return; }
    if (col.key === EARLIER) return;
    onSave(row, { start: col.key === NO_DATE ? '' : firstWorkingDay(col.key, today) });
  }

  if (!columns.length) return <div style={{ fontSize: 13, color: '#9ca3af', padding: 20 }}>Nothing to show.</div>;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${columns.length}, minmax(200px, 1fr))`, gap: 10, alignItems: 'start', overflowX: 'auto', paddingBottom: 8 }}>
      {columns.map(c => (
        <div key={c.key}
          onDragOver={canEdit ? e => { e.preventDefault(); if (over !== c.key) setOver(c.key); } : undefined}
          onDragLeave={() => { if (over === c.key) setOver(null); }}
          onDrop={canEdit ? e => drop(e, c) : undefined}
          style={{ background: over === c.key ? '#fff7ed' : 'var(--surface)', border: `1.5px solid ${over === c.key ? '#e87722' : 'var(--border)'}`, borderTop: `4px solid ${c.tint}`, borderRadius: 12, padding: 8, minHeight: 140 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '2px 4px 8px' }}>
            <span style={{ fontSize: 13, fontWeight: 800, color: c.tint }}>{c.label}</span>
            <span style={{ fontSize: 11.5, color: 'var(--text-muted)', marginLeft: 'auto' }}>{(byCol[c.key] || []).length}</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {(byCol[c.key] || []).map(r => (
              <div key={r.key} draggable={canEdit} onClick={() => onOpen(r)}
                onDragStart={e => { e.dataTransfer.setData('text/plain', JSON.stringify({ key: r.key })); e.dataTransfer.effectAllowed = 'move'; }}
                title={`${r.job_number} ${r.job_name}\n${r.type_label}${r.rig ? ' · ' + r.rig : ''}${r.pm_name ? ' · ' + r.pm_name : ''}`}
                style={{ background: '#fff', border: '1px solid #e5e7eb', borderLeft: `4px solid ${TINT[r.type]}`, borderRadius: 8, padding: '7px 9px', cursor: canEdit ? 'grab' : 'pointer', userSelect: 'none' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <span style={{ fontSize: 12.5, fontWeight: 800, color: TINT[r.type] }}>{r.job_number}</span>
                  <span style={{ fontSize: 12, color: '#111827', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{shortName(r.job_name) || r.job_name}</span>
                </div>
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <span>{r.type_label}{r.rig ? ` · ${r.rig}` : ''}</span>
                  {r.pm_name && <span>· {r.pm_name}</span>}
                </div>
                <div style={{ fontSize: 11, marginTop: 3, color: r.start ? '#374151' : '#c2410c', fontWeight: r.start ? 500 : 700 }}>
                  {r.start ? `${short(r.start)}${r.days ? ` → ${short(r.end)} · ${r.days}d` : ''}` : 'no date'}
                </div>
              </div>
            ))}
            {(byCol[c.key] || []).length === 0 && <div style={{ fontSize: 11.5, color: '#c4c8ce', textAlign: 'center', padding: '14px 0' }}>{canEdit && groupBy === 'month' && c.key !== EARLIER ? 'drop a card here' : '—'}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Calendar ─────────────────────────────────────────────────────────────────
// A wall calendar, Sunday first, the months from this one to the last scheduled finish.
// A card appears on every working day between its start and finish; a card with no
// length is shown on its start day. Drilling cards carry the drill's name, and two cards
// on the same drill on the same day go red. Weekends are greyed — nothing is counted
// there, the same rule the finish dates use.
function CalendarView({ rows, onOpen, onlyType = null }) {
  const [pick, setPick] = useState(null);     // a day, for the "more" list
  const today = ymd(new Date());

  const dated = useMemo(() => rows.filter(r => r.start && !r.done), [rows]);
  // day → cards on it
  const byDay = useMemo(() => {
    const m = new Map();
    for (const r of dated) {
      const end = r.end && r.end >= r.start ? r.end : r.start;
      for (const d = at(r.start); ymd(d) <= end; d.setDate(d.getDate() + 1)) {
        if (!isWork(d)) continue;
        const k = ymd(d);
        if (!m.has(k)) m.set(k, []);
        m.get(k).push(r);
      }
    }
    // Same drill twice on one day: both go red.
    for (const list of m.values()) {
      const seen = {};
      list.forEach(r => { if (r.rig) seen[r.rig] = (seen[r.rig] || 0) + 1; });
      list.forEach(r => { r._hot = !!(r.rig && seen[r.rig] > 1); });
      list.sort((a, b) => a.type_label.localeCompare(b.type_label) || String(a.rig || '~').localeCompare(String(b.rig || '~')) || String(a.job_number).localeCompare(String(b.job_number)));
    }
    return m;
  }, [dated]);

  const months = useMemo(() => {
    const first = new Date(); first.setDate(1); first.setHours(12, 0, 0, 0);
    let lastKey = ymd(first);
    for (const k of byDay.keys()) if (k > lastKey) lastKey = k;
    const out = [];
    for (let i = 0; i < 12; i++) {
      const m = new Date(first.getFullYear(), first.getMonth() + i, 1, 12);
      out.push(m);
      if (i >= 2 && ymd(new Date(m.getFullYear(), m.getMonth() + 1, 0, 12)) >= lastKey) break;
    }
    return out;
  }, [byDay]);

  const MAX = 4;
  const cell = { background: '#fff', border: '1px solid #eee', borderRadius: 8, padding: '5px 6px', minHeight: 64, display: 'flex', flexDirection: 'column', gap: 3 };
  const off = { ...cell, background: '#fafafa', border: '1px solid transparent', minHeight: 40 };
  const chip = r => ({
    fontSize: 11, fontWeight: 700, lineHeight: 1.25, borderRadius: 5, padding: '2px 6px', cursor: 'pointer',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
    border: `1px solid ${r._hot ? '#dc2626' : TINT[r.type]}`,
    background: r._hot ? '#fee2e2' : '#fff', color: r._hot ? '#991b1b' : TINT[r.type],
  });
  // Number, drill, then the name — the name takes whatever room is left and the whole
  // thing is in the hover (Pat: "add the name even if not all will fit").
  const label = r => `${r.job_number || ''}${r.rig ? ' · ' + r.rig : ''}${shortName(r.job_name) ? ' ' + shortName(r.job_name) : ''}`;
  const title = r => `${r.job_number} ${shortName(r.job_name) || r.job_name || ''}\n${r.type_label}${r.rig ? ' · ' + r.rig : ''}\n${short(r.start)} → ${short(r.end)}${r.days ? ` · ${r.days} working day${r.days === 1 || r.days === 0.5 ? '' : 's'}` : ' · no length set'}${r._hot ? '\n⚠ Double-booked on ' + r.rig : ''}`;
  const detail = pick ? (byDay.get(pick) || []) : [];

  if (dated.length === 0) return <div style={{ fontSize: 13, color: '#9ca3af', padding: 20 }}>No cards have a start date yet. Give them one on the List.</div>;

  return (
    <div>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 12, fontSize: 12 }}>
        {TYPES.filter(t => !onlyType || t.key === onlyType).map(t => <span key={t.key} style={{ display: 'flex', alignItems: 'center', gap: 5, color: 'var(--text-muted)' }}><span style={{ width: 10, height: 10, borderRadius: 3, background: t.tint }} /> {t.label}</span>)}
        {(!onlyType || /drilling/.test(onlyType)) && <span style={{ display: 'flex', alignItems: 'center', gap: 5, color: 'var(--text-muted)' }}><span style={{ width: 10, height: 10, borderRadius: 3, background: '#fee2e2', border: '1px solid #dc2626' }} /> same drill twice</span>}
      </div>

      {months.map(m => {
        const y = m.getFullYear(), mo = m.getMonth();
        const first = new Date(y, mo, 1), last = new Date(y, mo + 1, 0);
        const cells = [];
        for (let i = 0; i < first.getDay(); i++) cells.push(null);
        for (let d = 1; d <= last.getDate(); d++) cells.push(new Date(y, mo, d, 12));
        const count = new Set(cells.filter(Boolean).flatMap(c => (byDay.get(ymd(c)) || []).map(r => r.key))).size;
        return (
          <div key={`${y}-${mo}`} style={{ marginBottom: 18 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 6 }}>
              <span style={{ fontSize: 14, fontWeight: 800 }}>{m.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
              <span style={{ fontSize: 12, color: count ? '#6b7280' : '#9ca3af' }}>{count ? `${count} card${count === 1 ? '' : 's'}` : 'nothing scheduled'}</span>
            </div>
            {count > 0 && (<>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 4 }}>
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => <div key={d} style={{ fontSize: 11, color: '#9ca3af', fontWeight: 700 }}>{d}</div>)}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 4 }}>
                {cells.map((c, i) => {
                  if (!c) return <div key={i} />;
                  const key = ymd(c), list = byDay.get(key) || [], wknd = !isWork(c), isToday = key === today;
                  return (
                    <div key={i} style={{ ...(wknd ? off : cell), ...(isToday ? { border: '2px solid #111', padding: '4px 5px' } : null) }}>
                      <div style={{ fontSize: 11, color: isToday ? '#111' : '#9ca3af', fontWeight: isToday ? 800 : 400 }}>{c.getDate()}</div>
                      {list.slice(0, MAX).map(r => (
                        <div key={r.key} title={title(r)} onClick={() => onOpen(r)} style={chip(r)}>{label(r)}</div>
                      ))}
                      {list.length > MAX && (
                        <button onClick={() => setPick(key)} style={{ background: 'none', border: 'none', padding: 0, textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#2563eb', cursor: 'pointer', fontFamily: 'inherit' }}>
                          +{list.length - MAX} more
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </>)}
          </div>
        );
      })}

      {pick && (
        <div onClick={() => setPick(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 6200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: 18, width: 'min(520px, 96vw)', maxHeight: '80vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 12 }}>
              <b style={{ fontSize: 15 }}>{at(pick).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</b>
              <span style={{ fontSize: 13, color: '#6b7280', fontWeight: 700 }}>{detail.length} card{detail.length === 1 ? '' : 's'}</span>
              <button onClick={() => setPick(null)} style={{ marginLeft: 'auto', border: 'none', background: '#f3f4f6', borderRadius: 7, padding: '6px 12px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>Close</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {detail.map(r => (
                <div key={r.key} role="button" tabIndex={0} onClick={() => { setPick(null); onOpen(r); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 8, border: `1px solid ${r._hot ? '#fca5a5' : '#eee'}`, background: r._hot ? '#fef2f2' : '#fff', cursor: 'pointer' }}>
                  <span style={{ width: 10, height: 10, borderRadius: 3, background: TINT[r.type], flexShrink: 0 }} />
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#111' }}>{r.job_number}</span>
                  <span style={{ fontSize: 13, color: '#374151', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.job_name}</span>
                  <span style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>{r.type_label}{r.rig ? ` · ${r.rig}` : ''}{r.pm_name ? ` · ${r.pm_name}` : ''}</span>
                  {r._hot && <span style={{ fontSize: 11, fontWeight: 800, color: '#b91c1c' }}>⚠ same drill</span>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
